import dayjs from "dayjs";

import {
  datePickerFormat,
  dateTimeApiFormat,
  dateTimePickerFormat,
} from "../index";
import { intl } from "../containers/app";
import {
  parseBankForm,
  getDefaultBankForm,
  parseInitialBankValues,
} from "../components/registration/detail/bank";
import {
  parseAddressForm,
  getDefaultAddressForm,
  parseInitialAddressValues,
} from "../components/registration/detail/address";
import {
  parseContactsForm,
  parseInitialContactForm,
} from "../components/registration/detail/contacts";
import {
  parseProductsForm,
  parseInitialProductForm,
} from "../components/registration/detail/products";
import {
  parseEntityForm,
  getDefaultEntityForm,
  parseInitialEntityForm,
} from "../components/registration/detail/identificator";
import {
  pickupAddressInputId,
  invoiceAddressInputId,
  getDefaultAddressOrSameForm,
} from "../components/registration/detail/addressOrSame";
import {
  parseContactForm,
  getDefaultContactForm,
  parseInitialContactValues,
} from "../components/registration/detail/address/contact";
import {
  smsInputId,
  parseInitialSmsConfirmForm,
} from "../components/registration/detail/submit/smsConfirm";
import {
  parseFurtherInformationForm,
  getDefaultFurtherInformationForm,
  parseInitialFurtherInformationForm,
} from "../components/registration/detail/furtherInformation";

export const alphaNumericalZipCodes = ["GI"];

export const czechMobileRegex = "^(6|7)\\d{8}$";

/**
 * regexp pro validaci ve formu
 */
class Pattern {
  public pattern: string;
  public message: string;

  constructor(pattern: string, message: string) {
    this.pattern = pattern;
    this.message = message;
  }
}

/**
 * zpracuje datum a čas, pokud není zadaný, tak vrátí aktuální
 * @param dateTime datum a čas
 * @param date pouze datum
 */
export function parseDateTime(dateTime?: string, date?: boolean): string {
  return dateTime
    ? dayjs(dateTime).format(
        date === true ? datePickerFormat : dateTimePickerFormat
      )
    : "--";
}

export const isBoolean = (value: Boolean) => value === true || value === false;

/**
 * vrátí regexp pro format telefonního čísla dané země - https://cs.qaz.wiki/wiki/National_conventions_for_writing_telephone_numbers
 * @param countryCode kod země (CZ..)
 * @param onlyMobile jestli pro CZ vyzadovat pouze mobilni cislo
 */
export function getPhoneNumberPattern(
  countryCode: string,
  onlyMobile: boolean = false
): Pattern {
  switch (countryCode) {
    case "DK":
    case "NO":
    case "CY":
    case "BH":
    case "MD":
    case "BA":
      return new Pattern(
        "^\\d{8}$",
        intl.formatMessage({
          id: "address.validation.phone.eight",
          defaultMessage: "Zadejte telefonní číslo o délce 8!",
        })
      );
    case "CZ":
    case "SK":
    case "HU":
    case "PL":
    case "FR":
    case "PT":
      return onlyMobile && countryCode === "CZ"
        ? new Pattern(
            czechMobileRegex,
            intl.formatMessage({
              id: "address.validation.phone.czech",
              defaultMessage: "Zadejte validní české telefonní číslo!",
            })
          )
        : new Pattern(
            "^\\d{9}$",
            intl.formatMessage({
              id: "address.validation.phone.nine",
              defaultMessage:
                "Zadejte telefonní číslo v devítimístném formátu!",
            })
          );
    case "GB":
    case "GR":
    case "RU":
      return new Pattern(
        "^\\d{10}$",
        intl.formatMessage({
          id: "address.validation.phone.ten",
          defaultMessage: "Zadejte telefonní číslo o délce 10!",
        })
      );
    case "ES":
      return new Pattern(
        "^\\d{6,9}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "SE":
      return new Pattern(
        "^\\d{8,10}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "IE":
      return new Pattern(
        "^\\d{6,11}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "BE":
    case "NL":
    case "RO":
    case "CH":
      return new Pattern(
        "^\\d{9,10}$",
        intl.formatMessage({
          id: "address.validation.phone.nineTen",
          defaultMessage: "Zadejte telefonní číslo o délce 9 nebo 10!",
        })
      );
    case "IT":
      return new Pattern(
        "^\\d{3,12}$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
    case "DE":
      return new Pattern(
        "^\\d{6,12}$",
        intl.formatMessage({
          id: "address.validation.phone.eleven",
          defaultMessage: "Zadejte telefonní číslo o délce 6 až 12!",
        })
      );
    case "AT":
      return new Pattern(
        "^\\d{5,14}$",
        intl.formatMessage({
          id: "address.validation.phone.tenMore",
          defaultMessage: "Zadejte telefonní číslo o délce 5 až 12!",
        })
      );
    default:
      return new Pattern(
        "^\\d+$",
        intl.formatMessage({
          id: "address.validation.phone.others",
          defaultMessage:
            "Zadejte telefonní číslo v korektním formátu pro vybranou zemi!",
        })
      );
  }
}

/**
 * vrátí regexp pro format PSČ dané země
 * @param countryCode kod země (CZ..)
 */
export function getZipCodePattern(countryCode: string): Pattern {
  switch (countryCode) {
    case "CZ":
    case "SK":
    case "EE":
    case "ES":
    case "FR":
    case "IT":
    case "GR":
    case "SA":
    case "KW":
    case "JO":
    case "MA":
    case "RS":
    case "TR":
    case "UA":
    case "BA":
      return new Pattern(
        "^\\d{5}$",
        intl.formatMessage({
          id: "address.validation.zipCode.five",
          defaultMessage: "Zadejte PSČ v pětimístném formátu!",
        })
      );
    case "PL":
      return new Pattern(
        "^\\d{2}-\\d{3}$",
        intl.formatMessage({
          id: "address.validation.zipCode.poland",
          defaultMessage: "Zadejte PSČ ve správném formátu (XX-XXX)!",
        })
      );
    case "AT":
    case "HU":
    case "BE":
    case "BG":
    case "DK":
    case "CY":
    case "GE":
    case "MK":
    case "TN":
      return new Pattern(
        "^\\d{4}$",
        intl.formatMessage({
          id: "address.validation.zipCode.four",
          defaultMessage: "Zadejte PSČ v čtyřmístném formátu!",
        })
      );
    case "BH":
    case "OM":
      return new Pattern(
        "^\\d{3}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "HR":
    case "LT":
    case "FI":
    case "SE":
      return new Pattern(
        `^(${countryCode}-)?\\d{5}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.fiveCountry",
          defaultMessage:
            "Zadejte PSČ v pětimístném formátu s prefixem kódu státu a pomlčkou (XX-)!",
        })
      );
    case "LV":
    case "SI":
    case "MD":
      return new Pattern(
        `^(${countryCode}-)?\\d{4}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.fourCountry",
          defaultMessage:
            "Zadejte PSČ v čtyřmístném formátu s prefixem kódu státu a pomlčkou (XX-)!",
        })
      );
    case "LU":
      return new Pattern(
        `^(L-)?\\d{4}$`,
        intl.formatMessage({
          id: "address.validation.zipCode.four",
          defaultMessage: "Zadejte PSČ v čtyřmístném formátu!",
        })
      );
    case "RO":
    case "RU":
    case "KZ":
      return new Pattern(
        "^\\d{6}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "NL":
      return new Pattern(
        "^\\d{4}((-| )[A-Z]{2})?$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "PT":
      return new Pattern(
        "^\\d{4}-\\d{3}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "MT":
      return new Pattern(
        "^[A-Z]{3} \\d{4}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "IE":
      return new Pattern(
        "(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "US":
      return new Pattern(
        "^\\d{5}(?:[-\\s]\\d{4})?$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "GB":
      return new Pattern(
        "^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "DE":
      return new Pattern(
        "^(?!01000|99999)(0[1-9]\\d{3}|[1-9]\\d{4})$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "AE":
    case "YE":
    case "SY":
      return new Pattern(
        "^[A-Z]{2}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    case "QA":
    case "LB":
      return new Pattern(
        "^\\d{8}$",
        intl.formatMessage({
          id: "address.validation.zipCode.gbAndDe",
          defaultMessage: "Zadejte PSČ ve správném formátu!",
        })
      );
    default:
      return alphaNumericalZipCodes.includes(countryCode)
        ? new Pattern(
            "^[A-Za-z0-9 ]+$",
            intl.formatMessage({
              id: "address.validation.zipCode.alphanumeric",
              defaultMessage: "PSČ musí být alfanumerické!",
            })
          )
        : new Pattern(
            "^\\d+$",
            intl.formatMessage({
              id: "address.validation.zipCode.others",
              defaultMessage:
                "Zadejte PSČ v korektním formátu pro vybranou zemi!",
            })
          );
  }
}

export const parseRegistrationForm = (
  id: string,
  values: any,
  fingerprint: string,
  termAgreeDateTime: string,
  //personalDataAgreeDateTime: string,
  sent: boolean
) => ({
  id,
  entity: parseEntityForm(values),
  principal: {
    ...parseContactForm(values),
    address: parseAddressForm(values),
  },
  bankAccount: parseBankForm(values),
  invoiceAddress: values.sameInvoiceAddress
    ? undefined
    : parseAddressForm(values, invoiceAddressInputId),
  invoiceAddressSameAsPrincipal: values.sameInvoiceAddress,
  pickupAddress: values.samePickupAddress
    ? undefined
    : parseAddressForm(values, pickupAddressInputId),
  pickupAddressSameAsPrincipal: values.samePickupAddress,
  contacts: parseContactsForm(values),
  products: parseProductsForm(values),
  ...parseFurtherInformationForm(values),
  agreeWithTerms: termAgreeDateTime,
  //agreeWithPersonalData: personalDataAgreeDateTime,
  fingerprint,
  sent: sent ? { date: dayjs().format(dateTimeApiFormat) } : undefined,
  smsCode: values.smsCode,
  password: values.password,
});

export const parseInitialFormValues = (contract: any, password: string) => {
  if (contract) {
    return {
      ...parseInitialEntityForm(contract.entity),
      ...parseInitialContactValues(contract.principal),
      ...parseInitialAddressValues(
        contract.principal && contract.principal.address
      ),
      ...parseInitialBankValues(contract.bankAccount),
      ...parseInitialAddressValues(
        contract.invoiceAddress,
        invoiceAddressInputId
      ),
      sameInvoiceAddress: isBoolean(contract.invoiceAddressSameAsPrincipal)
        ? contract.invoiceAddressSameAsPrincipal
        : true,
      ...parseInitialAddressValues(
        contract.pickupAddress,
        pickupAddressInputId
      ),
      samePickupAddress: isBoolean(contract.pickupAddressSameAsPrincipal)
        ? contract.pickupAddressSameAsPrincipal
        : true,
      ...parseInitialContactForm(contract.contacts),
      ...parseInitialProductForm(contract.products),
      ...parseInitialFurtherInformationForm(contract),
      termsAndConditions: contract.agreeWithTerms ? true : false,
      personalData: contract.agreeWithPersonalData ? true : false,
      ...parseInitialSmsConfirmForm(contract.principal),
      password,
    };
  } else {
    return {
      ...getDefaultEntityForm(),
      ...getDefaultContactForm(),
      ...getDefaultAddressForm(),
      ...getDefaultBankForm(),
      ...getDefaultAddressOrSameForm(),
      ...getDefaultFurtherInformationForm(),
      ...getDefaultContactForm(smsInputId),
    };
  }
};

export const toLower = (str: string) =>
  str ? str.substring(0, 1).trim() + str.substring(1).toLowerCase().trim() : "";

export const decodeCourtName = (court: string) => {
  switch (court) {
    case "BR":
      return "Praha";
    case "CB":
      return "České Budějovice";
    case "HK":
      return "Hradec Králové";
    case "OS":
      return "Ostrava";
    case "PL":
      return "Plzeň";
    case "UL":
      return "Ústí nad Labem";
    case "PH":
      return "Praha";
    default:
      return "Praha";
  }
};
